<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Print Button and Selection Actions -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>{{ $t("lab.test-invoices") }}</h2>
              <div class="d-flex gap-2">
                <b-button
                  v-if="!allSelected"
                  variant="outline-primary"
                  size="sm"
                  @click="selectAll"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.select-all") }}
                </b-button>
                <b-button
                  v-if="canBulkApprovePending"
                  variant="warning"
                  size="sm"
                  @click="showBulkUpdateModal"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1"
                  />
                  الموافقة على الفواتير ({{ selectedInvoices.length }})
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="success"
                  size="sm"
                  @click="bulkPrintSummary"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-list") }}
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="info"
                  size="sm"
                  @click="bulkPrintDetailed"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-detailed") }}
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearSelection"
                >
                  {{ $t("common.clear-selection") }}
                </b-button>
              </div>
            </div>

            <!-- Bulk Selection Summary -->
            <b-row
              v-if="selectedInvoices.length > 0"
              class="mb-3"
            >
              <b-col cols="12">
                <b-alert
                  show
                  variant="info"
                  class="mb-0"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ $t("common.selected") }}: {{ selectedInvoices.length }} {{ $t("common.invoices") }}</strong>
                    </div>
                    <div>
                      <strong>{{ $t("common.total") }}: <span class="text-success">{{ formatPrice(selectedInvoicesTotal) }}</span></strong>
                    </div>
                  </div>
                </b-alert>
              </b-col>
            </b-row>

            <!-- Data Table -->
            <b-table
              v-if="!loading"
              :items="invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
            >
              <!-- Checkbox for selection -->
              <template #cell(select)="data">
                <b-form-checkbox
                  v-model="selectedInvoices"
                  :value="data.item"
                  @change="onSelectionChange"
                />
              </template>

              <template #cell(invoice_no)="data">
                <span class="font-weight-bold text-primary">
                  #{{ getNestedProperty(data.item, 'invoice_no') }}
                </span>
              </template>

              <template #cell(test_items)="data">
                <div
                  v-if="getNestedProperty(data.item, 'invoice_items.length', 0) > 0"
                  class=""
                >
                  <!-- Show first item only -->
                  <div
                    v-for="(item, index) in data.item.invoice_items.slice(0, 1)"
                    :key="index"
                    class="test-item-row"
                  >
                    <div class="d-flex justify-content-between align-items-center py-1">
                      <div class="test-item-name">
                        <b-badge
                          variant="primary"
                          class=" mr-1"
                          style="font-size: 0.85rem;"
                        >
                          {{ index + 1 }}
                        </b-badge>
                        <span class="font-weight-bold text-dark">
                          {{ getNestedProperty(item, 'name', 'N/A') }}
                        </span>
                      </div>
                      <div class="test-item-amount">
                        <span
                          class="text-success font-weight-bolder"
                          style="font-size: 0.95rem;"
                        >
                          {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Show more button if more than 1 item -->
                  <div
                    v-if="data.item.invoice_items.length > 1"
                    class=""
                  >
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="text-nowrap"
                      block
                      @click="showAllTestItems(data.item)"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ $t('common.show') }} {{ data.item.invoice_items.length - 1 }} {{ $t('common.more-items') }}
                    </b-button>
                  </div>
                </div>
                <span
                  v-else
                  class="text-muted"
                >{{ $t('common.no-items') }}</span>
              </template>

              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bolder text-success">
                  {{ formatPrice(data.item.amount) }}
                </span>
              </template>

              <template #cell(discount)="data">
                <span class="text-nowrap font-weight-bold text-warning">
                  {{ formatPrice(data.item.discount || 0) }}
                </span>
              </template>

              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="testInvoice"
                    endpoint="invoices"
                    :data="data.item"
                    @onEmit="$emit('onChangeStatus')"
                  />
                </div>
              </template>

              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved'"
                    variant="outline-primary"
                    size="sm"
                    class="mx-1"
                    @click="showTestInvoice(data.item)"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-button
                    v-if="hasPermission('apply-invoice-discount') && data.item.status !== 'printed' && data.item.status !== 'approved'"
                    variant="outline-warning"
                    size="sm"
                    class="mx-1"
                    @click="showDiscountModal(data.item)"
                  >
                    <feather-icon
                      icon="PercentIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.apply-discount") }}
                  </b-button>
                </div>
              </template>
            </b-table>

            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>

            <!-- Pagination -->
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span class="text-muted">
                    {{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  @click="getInvoices()"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Single Invoice Print Modal -->
      <b-modal
        id="modal-confirm-print-test"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printSingleInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- Bulk Update Status Modal -->
      <b-modal
        id="modal-bulk-update-status"
        :no-close-on-backdrop="true"
        title="الموافقة على الفواتير المعلقة"
        ok-only
        ok-title="الموافقة على الفواتير"
        @ok="bulkUpdateStatus"
      >
        <b-card-text>
          <h5>الموافقة على الفواتير المعلقة</h5>
          <p>{{ $t('common.selected-invoices-count', { count: selectedInvoices.length }) }}</p>

          <p class="font-weight-bold">
            هل تريد الموافقة على جميع الفواتير المعلقة المحددة؟
          </p>

          <div class="mt-3">
            <h6>{{ $t('common.selected-invoices') }}:</h6>
            <ul class="list-unstyled">
              <li
                v-for="invoice in selectedInvoices"
                :key="invoice.id"
                class="mb-1"
              >
                <small class="text-muted">
                  #{{ getNestedProperty(invoice, 'invoice_no') }} -
                  {{ getTestName(invoice) }} -
                  {{ formatPrice(getNestedProperty(invoice, 'amount')) }}
                </small>
              </li>
            </ul>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group
            :label="$t('common.invoice-number')"
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.current-amount')"
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatPrice(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.discount-amount')"
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert
            v-if="discountForm.discount > 0"
            variant="info"
            show
            class="mt-2"
          >
            <div class="d-flex justify-content-between">
              <span>{{ $t('common.amount-after-discount') }}:</span>
              <strong>{{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>

      <!-- Modal to show all test items -->
      <b-modal
        id="modal-all-test-items"
        centered
        size="md"
        :title="$t('common.all-items') + ' - ' + $t('common.invoice-number') + ' #' + selectedInvoiceNo"
        hide-footer
      >

        <b-card-text>
          <div v-if="selectedInvoiceItems.length > 0">
            <div
              v-for="(item, index) in selectedInvoiceItems"
              :key="getTestItemKey(item, index)"
              class="test-item-modal-row"
              :class="{ 'border-bottom': index < selectedInvoiceItems.length - 1 }"
            >
              <div class="d-flex justify-content-between align-items-center py-2">
                <div class="d-flex align-items-center flex-1">
                  <b-badge
                    variant="primary"
                    class="mr-2"
                    style="font-size: 0.9rem;"
                  >
                    {{ index + 1 }}
                  </b-badge>
                  <span class="font-weight-bold text-dark">
                    {{ getNestedProperty(item, 'name', 'N/A') }}
                  </span>
                </div>
                <div class="d-flex align-items-center ml-2">
                  <span
                    class="text-success font-weight-bolder mr-2"
                    style="font-size: 1rem;"
                  >
                    {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                  </span>
                  <b-button
                    v-if="selectedInvoiceIsPending && getPatientCardTestId(item)"
                    variant="outline-danger"
                    size="sm"
                    class="text-nowrap"
                    :disabled="deletingTestId === getPatientCardTestId(item)"
                    @click="confirmDeleteTestItem(item, index)"
                  >
                    <b-spinner
                      v-if="deletingTestId === getPatientCardTestId(item)"
                      small
                      class="mr-1"
                    />
                    <feather-icon
                      v-else
                      icon="Trash2Icon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t('common.delete') }}
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Total -->
            <div class="mt-3 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <span
                  class="font-weight-bold text-dark"
                  style="font-size: 1.1rem;"
                >
                  {{ $t('common.total') }}:
                </span>
                <span
                  class="text-success font-weight-bolder"
                  style="font-size: 1.2rem;"
                >
                  {{ formatPrice(calculateModalTotal()) }}
                </span>
              </div>
            </div>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Hidden print button for multiple invoices -->
      <b-button
        ref="TestInvoiceMultiple"
        v-print="TestInvoiceMultiple"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-selected") }}
      </b-button>

      <!-- Printable Components -->
      <div ref="printContent">
        <TestInvoice
          :patient-data="patientData"
          :data="currentInvoice"
          :test-map="testMap"
        />
      </div>

      <InvoiceSummaryPrint
        id="BulkPrintSummaryTest"
        :invoices="bulkPrintInvoicesData"
        :total-amount="bulkPrintTotal"
        invoice-type="test"
      />

      <!-- Shared Invoice List PDF Component -->
      <InvoicesListPdf
        v-if="listPrintItemsTest.length > 0"
        ref="testInvoicesListPdf"
        :patient-name="listPrintPatientName"
        :print-date="new Date()"
        :items="listPrintItemsTest"
        :title="$t('lab.test-invoices')"
        filename="test-invoices-list"
        @hasStartedGeneration="isGeneratingPdf = true"
        @hasGenerated="isGeneratingPdf = false"
      />

      <!-- Loading Overlay -->
      <div
        v-if="isGeneratingPdf"
        class="pdf-loading-overlay"
      >
        <div class="pdf-loading-content">
          <b-spinner
            variant="primary"
            label="Loading..."
          />
          <p class="mt-2">
            {{ $t("common.generating-pdf") || "جاري إنشاء PDF..." }}
          </p>
        </div>
      </div>

      <!-- Bulk Print Detailed Components -->
      <div id="BulkPrintTestInvoices">
        <div
          v-for="inv in bulkPrintInvoicesData"
          :key="`bulk-test-${inv.id}`"
          class="print-page-break"
        >
          <TestInvoice
            :patient-data="getBulkPatientDataForTest(inv)"
            :data="inv"
            :test-map="testMap"
          />
        </div>
      </div>

      <!-- Hidden print buttons -->
      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintSummaryRef"
        v-print="BulkPrintSummaryConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-summary") }}
      </b-button>

      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintRef"
        v-print="BulkPrintConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-detailed") }}
      </b-button>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BTable,
  BBadge,
  BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getNestedProperty } from '@/utils/nestedProperty.js'

const TestInvoice = () => import('@/components/printables/TestInvoice.vue')
const TestInvoiceMultiple = () => import('@/components/printables/TestInvoiceMultiple.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')
const InvoiceSummaryPrint = () => import('@/components/printables/InvoiceSummaryPrint.vue')
const InvoicesListPdf = () => import('@/components/printables/InvoicesListPdf.vue')

export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    ToastificationContent,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    BAlert,
    ChangeInvoiceStatusDropDown,
    TestInvoice,
    TestInvoiceMultiple,
    InvoiceSummaryPrint,
    InvoicesListPdf,
  },
  props: ['invoices', 'patientData'],
  data() {
    return {
      tableColumns: [
        {
          key: 'select',
          label: '',
          thStyle: { width: '50px' },
        },
        {
          key: 'invoice_no',
          sortable: true,
          label: this.$t('hospital.invoice-no'),
        },
        {
          key: 'test_items',
          label: this.$t('lab.test-name'),
          sortable: false,
        },
        {
          key: 'discount',
          label: this.$t('common.discount'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('employee.amount'),
          sortable: true,
        },
        {
          key: 'created_at',
          sortable: true,
          label: this.$t('common.created-at'),
        },
        {
          key: 'status',
          sortable: true,
          label: 'Status',
        },
        { key: 'actions', label: this.$t('common.actions') },
      ],
      currentInvoice: null,
      selectedInvoices: [],
      selectedInvoiceItems: [], // For modal display
      selectedInvoiceNo: '', // For modal title
      selectedInvoiceForModal: null, // Invoice when modal opened (for refresh after delete)
      selectedInvoiceIsPending: false, // Show delete only when invoice status is pending
      deletingTestId: null, // ID of test being deleted (for spinner)
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0,
      },
      discountError: '',
      testMap: {},
      bulkUpdateForm: {
        status: null,
      },
      statusOptions: [],
      isGenerating: false,
      isGeneratingPdf: false,
      TestInvoiceMultiple: {
        id: 'TestInvoiceMultiple',
      },
      page: 1,
      dataCount: 0,
      take: 10,
      loading: false,
      bulkPrintInvoicesData: [],
      BulkPrintConfig: {
        id: 'BulkPrintTestInvoices',
      },
      BulkPrintSummaryConfig: {
        id: 'BulkPrintSummaryTest',
      },
    }
  },

  computed: {
    allSelectedInvoicesApproved() {
      return this.selectedInvoices.length > 0
             && this.selectedInvoices.every(invoice => invoice.status === 'approved')
    },

    allSelected: {
      get() {
        const invoicesData = getNestedProperty(this.invoices, 'data', [])
        return invoicesData.length > 0 && this.selectedInvoices.length === invoicesData.length
      },
      set(value) {
        // Handled by toggleSelectAll
      },
    },
    hasPermission() {
      return permission => this.$store.state.app.permissions
               && Array.isArray(this.$store.state.app.permissions)
               && this.$store.state.app.permissions.includes(permission)
    },
    canBulkPrint() {
      if (this.selectedInvoices.length === 0) return false
      return this.selectedInvoices.every(invoice => invoice.status === 'approved')
    },
    canBulkApprovePending() {
      if (this.selectedInvoices.length === 0) return false
      return this.selectedInvoices.every(invoice => {
        const status = (invoice.status || '').toLowerCase()
        return status === 'pending'
      })
    },
    selectedInvoicesTotal() {
      return this.selectedInvoices.reduce((total, invoice) => {
        const amount = getNestedProperty(invoice, 'amount', 0)
        return total + (parseFloat(amount) || 0)
      }, 0)
    },
    bulkPrintTotal() {
      return this.bulkPrintInvoicesData.reduce((total, invoice) => total + (Number(invoice.amount) || 0), 0)
    },

    listPrintItemsTest() {
      if (!this.bulkPrintInvoicesData || this.bulkPrintInvoicesData.length === 0) {
        return []
      }
      return this.bulkPrintInvoicesData.map(invoice => ({
        invoice_no: invoice.invoice_no,
        patient_name: getNestedProperty(invoice, 'patient_card.patient.name', 'N/A'),
        // Prefer the same source used in the main table (first invoice_items name),
        // fall back to getTestName for older data shapes
        name: getNestedProperty(invoice, 'invoice_items.0.name', this.getTestName(invoice)),
        amount: invoice.amount || 0,
        discount: invoice.discount || 0,
        date: invoice.date || invoice.created_at,
        created_at: invoice.created_at,
        id: invoice.id,
      }))
    },

    listPrintPatientName() {
      if (this.patientData && this.patientData.name) {
        return this.patientData.name
      }
      if (this.bulkPrintInvoicesData && this.bulkPrintInvoicesData.length > 0) {
        const firstInvoice = this.bulkPrintInvoicesData[0]
        return getNestedProperty(firstInvoice, 'patient_card.patient.name', 'N/A')
      }
      return 'N/A'
    },
  },

  mounted() {
    this.loadTestMap()
  },

  activated() {
    this.$emit('onChangeStatus')
    this.loadTestMap()
  },

  methods: {
    getNestedProperty,

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },

    // Show all test items in modal
    showAllTestItems(invoice) {
      this.selectedInvoiceForModal = invoice
      this.selectedInvoiceItems = getNestedProperty(invoice, 'invoice_items', [])
      this.selectedInvoiceNo = invoice.invoice_no || ''
      this.selectedInvoiceIsPending = (getNestedProperty(invoice, 'status', '') || '').toString().toLowerCase() === 'pending'
      this.deletingTestId = null
      this.$bvModal.show('modal-all-test-items')
    },

    getPatientCardTestId(item) {
      return getNestedProperty(item, 'itemable_id') || getNestedProperty(item, 'itemable.id') || getNestedProperty(item, 'patient_card_test_id') || (item.itemable && item.itemable.id) || null
    },

    getTestItemKey(item, index) {
      const id = this.getPatientCardTestId(item)
      return id || `item-${index}`
    },

    confirmDeleteTestItem(item, index) {
      const id = this.getPatientCardTestId(item)
      if (!id) return
      const testName = getNestedProperty(item, 'name', this.$t('lab.test'))
      this.$bvModal.msgBoxConfirm(
        this.$t('common.delete-sure') || `هل أنت متأكد من حذف "${testName}"؟`,
        {
          title: this.$t('common.delete') || 'حذف',
          okVariant: 'danger',
          okTitle: this.$t('common.yes') || 'نعم',
          cancelTitle: this.$t('common.cancel') || 'إلغاء',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(confirmed => {
        if (confirmed) this.deleteTestItem(id, index)
      })
    },

    async deleteTestItem(patientCardTestId, index) {
      this.deletingTestId = patientCardTestId
      try {
        await this.$http.delete(`/patientCardTest/${patientCardTestId}`)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.delete_success') || this.$t('common.deleted-successfully'),
          },
        })
        this.selectedInvoiceItems.splice(index, 1)
        this.$emit('onChangeStatus')
        if (this.selectedInvoiceItems.length === 0) {
          this.$bvModal.hide('modal-all-test-items')
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: (error.response && error.response.data && error.response.data.message) || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.deletingTestId = null
      }
    },

    // Calculate total for modal
    calculateModalTotal() {
      return this.selectedInvoiceItems.reduce((sum, item) => {
        const amount = getNestedProperty(item, 'net_amount', 0)
        return sum + (parseFloat(amount) || 0)
      }, 0)
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
      }
      this.discountError = ''
      this.$bvModal.show('modal-apply-discount')
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0
      const discount = parseFloat(this.discountForm.discount) || 0
      return Math.max(0, amount - discount)
    },

    // Apply discount to invoice
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Validate discount
      const discount = parseFloat(this.discountForm.discount)
      const amount = parseFloat(this.discountForm.amount)

      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount')
        return
      }

      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount')
        return
      }

      try {
        this.loading = true

        const payload = {
          discount,
        }

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.discount-applied-successfully'),
          },
        })

        this.$bvModal.hide('modal-apply-discount')

        // Refresh data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error applying discount:', error)

        // Handle validation errors (422)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0]
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errors.discount[0],
              },
            })
          } else {
            // Generic validation error
            const firstError = Object.values(errors)[0]
            const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
            this.discountError = errorMessage
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errorMessage,
              },
            })
          }
        } else {
          // Generic error
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.discount-apply-error'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },

    async loadTestMap() {
      try {
        const response = await this.$http.get('/test?take=1000')
        const tests = getNestedProperty(response, 'data.data.data', [])

        // Create a map of test_id to test_name
        this.testMap = tests.reduce((map, test) => {
          map[test.id] = test.name
          return map
        }, {})
      } catch (error) {
        console.error('Error loading test map:', error)
        this.$toast.error(this.$t('common.error-occurred'))
      }
    },

    getTestName(invoice) {
      // 1. Try direct test_name property
      if (invoice.test_name) return invoice.test_name

      // 2. Try nested patient_card_test.test.name
      const nestedName = getNestedProperty(invoice, 'patient_card_test.test.name')
      if (nestedName && nestedName !== 'N/A') return nestedName

      // 3. Try using testMap with test_id from patient_card_test
      const testId = getNestedProperty(invoice, 'patient_card_test.test_id')
      if (testId && this.testMap[testId]) return this.testMap[testId]

      return 'N/A'
    },

    showTestInvoice(item) {
      this.currentInvoice = item
      this.$bvModal.show('modal-confirm-print-test')
    },

    async printSingleInvoice() {
      try {
        // Trigger print
        this.generatePdf()

        // Update status to printed
        await this.$http.put(`/invoices/${this.currentInvoice.id}`, {
          status: 'printed',
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.invoice-printed-successfully'),
          },
        })

        // Refresh data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating invoice status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-updating-status'),
          },
        })
      }
    },

    generatePdf() {
      this.isGenerating = true
      const content = this.$refs.printContent
      if (!content) {
        this.isGenerating = false
        return
      }

      // Get the innerHTML of the TestInvoice component
      const invoiceElement = content.querySelector('#TestInvoice')
      const htmlContent = invoiceElement ? invoiceElement.innerHTML : content.innerHTML

      // Get all styles from the document
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      const fullHtmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Test Invoice</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      direction: rtl;
    }
    #TestInvoice {
      display: block !important;
      visibility: visible !important;
    }
    .invoice-container {
      padding: 10px;
      font-family: Arial, sans-serif;
      width: 100%;
      max-width: none;
    }
    @media print {
      body {
        padding: 10mm;
        margin: 0;
      }
      #TestInvoice {
        display: block !important;
      }
      .invoice-container {
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 5px !important;
      }
      table {
        width: 100% !important;
        font-size: 12px !important;
      }
      th, td {
        padding: 6px !important;
        font-size: 12px !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    ${styles}
  </style>
</head>
<body>
  ${htmlContent}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      // Open in a new tab/window and write content
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        alert(
          this.$t('common.popup-blocked') || 'Please allow popups for this site',
        )
        this.isGenerating = false
        return
      }

      // Write content to the new window
      printWindow.document.open()
      printWindow.document.write(fullHtmlContent)
      printWindow.document.close()

      // Focus on the new window after a short delay to ensure it's loaded
      setTimeout(() => {
        printWindow.focus()
      }, 100)

      // Listen for print window close
      const checkClosed = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkClosed)
          this.isGenerating = false
        }
      }, 500)

      // Also handle after print dialog closes
      printWindow.onbeforeunload = () => {
        clearInterval(checkClosed)
        this.isGenerating = false
      }

      // Reset generating state after a delay
      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },

    async printSelectedInvoices() {
      if (this.selectedInvoices.length === 0) {
        this.$toast.error(this.$t('common.select-invoices-first'))
        return
      }

      try {
        // Trigger print
        this.$refs.TestInvoiceMultiple.click()

        // Update all selected invoices to printed status
        const updatePromises = this.selectedInvoices.map(invoice => this.$http.put(`/invoices/${invoice.id}`, {
          status: 'printed',
        }))

        await Promise.all(updatePromises)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.invoices-printed-successfully'),
          },
        })

        // Clear selection and refresh data
        this.clearSelection()
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating invoices status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-updating-status'),
          },
        })
      }
    },

    clearSelection() {
      this.selectedInvoices = []
    },

    selectAll() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      this.selectedInvoices = [...invoicesData]
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectAll()
      } else {
        this.clearSelection()
      }
    },

    showBulkUpdateModal() {
      if (this.selectedInvoices.length === 0) {
        this.$toast.error(this.$t('common.select-invoices-first'))
        return
      }

      // Allow bulk approve only for pending invoices
      const hasNonPending = this.selectedInvoices.some(invoice => {
        const status = (invoice.status || '').toLowerCase()
        return status !== 'pending'
      })

      if (hasNonPending) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: 'لا يوجد إجراء متاح على هذه الفواتير (يجب أن تكون الفواتير بحالة معلق فقط)',
          },
        })
        return
      }

      this.bulkUpdateForm.status = 'approved'
      this.$bvModal.show('modal-bulk-update-status')
    },

    async bulkUpdateStatus() {
      // Status check removed as we default to approved

      try {
        this.loading = true

        // Prepare the payload
        const payload = {
          invoices: this.selectedInvoices.map(invoice => ({
            id: invoice.id,
            status: 'approved',
          })),
        }

        console.log('Bulk update payload:', payload)

        // Call the bulk update API
        await this.$http.post('/invoices/bulkUpdateStatus', payload)

        this.$toast.success(this.$t('common.bulk-update-success'))

        // Clear selection and refresh data
        this.clearSelection()
        this.$bvModal.hide('modal-bulk-update-status')

        // Emit event to refresh parent data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error bulk updating invoices:', error)
        this.$toast.error(this.$t('common.bulk-update-error'))
      } finally {
        this.loading = false
      }
    },

    onSelectionChange() {
      // Handle selection change if needed
    },
    async bulkPrintSummary() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.isGeneratingPdf = true

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'patientCardTest,invoiceItems,patientCardTest.test,patientCard'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          // Use the new shared PDF component
          setTimeout(() => {
            if (this.$refs.testInvoicesListPdf) {
              this.$refs.testInvoicesListPdf.generatePdf()
            }
          }, 100)

          // Safety timeout: ensure loading state clears even if preview is closed manually
          setTimeout(() => {
            if (this.isGeneratingPdf) {
              this.isGeneratingPdf = false
            }
          }, 15000)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.isGeneratingPdf = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async bulkPrintDetailed() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'patientCardTest,invoiceItems,patientCardTest.test,patientCard'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          setTimeout(() => {
            if (this.$refs.BulkPrintRef) {
              this.$refs.BulkPrintRef.click()
            } else {
              window.print()
            }

            setTimeout(() => {
              this.updateBulkPrintStatus()
            }, 1000)
          }, 500)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async updateBulkPrintStatus() {
      if (this.selectedInvoices.length === 0) return

      try {
        const invoicesPayload = this.selectedInvoices.map(invoice => ({
          id: invoice.id,
          status: 'printed',
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.clearSelection()
        this.bulkPrintInvoicesData = []
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating bulk print status:', error)
      }
    },
    getBulkPatientDataForTest(invoice) {
      if (invoice && invoice.patient_card && invoice.patient_card.patient) {
        return { ...invoice.patient_card.patient }
      }
      return this.patientData || {}
    },
  },
}
</script>

<style lang="scss">
@media print {
  .print-page-break {
    page-break-after: always;
    display: block;
  }
}

.hidden {
  display: none !important;
}

.gap-2 {
  gap: 0.5rem;
}

/* Test Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

.badge-light-primary {
  /* background-color: rgba(115, 103, 240, 0.12); */
  /* color: #7367f0; */
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.pdf-loading-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}
</style>
